import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { L, imgOf, useFetch, useTitle } from "../lib";
import { PageHeader, SampleTag, Empty, Lightbox, ArtImage, Reveal } from "../ui";

const colCount = () => (window.innerWidth >= 1280 ? 4 : window.innerWidth >= 768 ? 3 : 2);

const useCols = () => {
  const [n, setN] = useState(colCount);
  useEffect(() => {
    const on = () => setN(colCount());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return n;
};

export default function Photos() {
  useTitle("Photographs");
  const { albumId } = useParams();
  const { data: albums } = useFetch("/albums");
  const { data: photos } = useFetch(`/photos${albumId ? `?album_id=${albumId}` : ""}`, [albumId]);
  const [lb, setLb] = useState(null);
  const n = useCols();
  const cols = Array.from({ length: n }, (_, c) => (photos || []).map((p, i) => [p, i]).filter(([, i]) => i % n === c));
  const album = albums?.find((a) => a.id === albumId);
  const items = (photos || []).map((p) => ({ src: imgOf(p), title: p.caption, caption: [p.date_text, p.location].filter(Boolean).join(" · ") }));
  return (
    <div data-testid="photos-page">
      <PageHeader eyebrow="Photographs" title={album ? album.name : "A life in pictures"}
        intro={album?.description || "Albums gathered by the family and by everyone who has shared a photograph."} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <nav aria-label="Albums" className="flex flex-wrap gap-2 border-b border-lou-line pb-5">
          <Link to={L("/photographs")} data-testid="album-all"
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${!albumId ? "bg-lou-ink text-lou-paper border-lou-ink" : "border-lou-line hover:border-lou-clay"}`}>All</Link>
          {(albums || []).map((a) => (
            <Link key={a.id} to={L(`/photographs/album/${a.id}`)} data-testid={`album-${a.id}`}
              className={`text-sm px-4 py-2 rounded-full border transition-colors ${albumId === a.id ? "bg-lou-ink text-lou-paper border-lou-ink" : "border-lou-line hover:border-lou-clay"}`}>
              {a.name} <span className="opacity-60">{a.count}</span>
            </Link>
          ))}
          <Link to={L("/share?type=photos")} className="ml-auto lou-btn !py-2 text-sm" data-testid="photos-share-cta">Add photographs</Link>
        </nav>
        <div className="mt-10">
          {photos && photos.length === 0 && <Empty testid="photos-empty">No photographs here yet.</Empty>}
          <div className="flex gap-4 md:gap-6 items-start" data-testid="photos-grid">
            {cols.map((col, c) => (
              <div key={c} className="flex-1 min-w-0 space-y-4 md:space-y-6">
                {col.map(([p, i]) => (
                  <Reveal key={p.id} delay={c * 0.04}>
                    <figure className="group" data-testid={`photo-card-${p.id}`}>
                      <button onClick={() => setLb(i)} className="block w-full relative" aria-label={`View ${p.caption || "photograph"} full screen`} data-testid={`photo-open-${p.id}`}>
                        <ArtImage src={imgOf(p)} alt={p.caption || "Photograph"} />
                        <SampleTag show={p.is_sample} className="absolute top-2 left-2" />
                      </button>
                      {p.caption && <figcaption className="mt-2 text-sm">{p.caption}</figcaption>}
                    </figure>
                  </Reveal>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Lightbox items={items} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
    </div>
  );
}
